import type { DragActivation } from "../new/ProductViewerConfigContext";
import type { GlowVariant } from "./glowTintMaterial";

/** Everything the visual state of a draggable part is derived from. */
export interface DragVisualsInput {
  /** How a drag is started on this part: `"direct"` means it is armed. */
  activation: DragActivation;
  /**
   * The host is pointing this part out - its row is hovered in a list. Draws
   * the full glow whether or not the part is armed. A press still follows
   * `activation`.
   */
  pointedOut: boolean;
  /**
   * The host has selected this part - it is the one being worked on. Carries
   * the direction arrows, and in direct mode it is the part a plain press
   * drags; the camera is the host's business.
   */
  selected: boolean;
  /** Whether the part accepts pointer interaction at all. */
  enabled: boolean;
  /** Whether the consumer allows the hover glow on this part. */
  showHoverHighlight: boolean;
  /** `constraint.activateOnly`: the part opens a menu but never moves. */
  activateOnly: boolean;
  /** Debug mode draws every helper unconditionally. */
  debug: boolean;
  /** Pointer is over the part. */
  isHovered: boolean;
  /**
   * The user is orbiting the camera. Hover counts for nothing while it holds:
   * parts sweep under the cursor, and lighting each one made the orbit
   * stutter. What the host points out and selects is unaffected.
   */
  cameraOrbiting: boolean;
  /** ⌘/Ctrl is held down. */
  modifierHeld: boolean;
  /** This part's own drag is running (local state, clears on pointer up). */
  isDragging: boolean;
  /** The store still names this part as the dragged one (lags by 100 ms). */
  draggingThis: boolean;
  /** Some part - not necessarily this one - is being dragged. */
  isDragActive: boolean;
}

/** Which of a draggable part's visual layers are drawn right now. */
export interface DragVisuals {
  /** The fresnel glow around the part's silhouette, and how strong. */
  glow: "none" | GlowVariant;
  /** The glow also draws where the case occludes the part. */
  glowXray: boolean;
  /** The world-aligned direction-arrow cross at the part. */
  arrows: boolean;
  /** The movement-area outline and the centre/diagonal guide lines. */
  guides: boolean;
  /** What the shared canvas cursor should show, if this part claims it. */
  cursor: "grabbing" | "move" | null;
}

/**
 * Which glow layer the part draws, and how strong.
 *
 * Suppressed during this part's own drag: the outline and guides take over,
 * and the glow would otherwise refit its mesh clones on every frame.
 */
function resolveGlow(
  input: DragVisualsInput,
  armed: boolean,
  marked: boolean,
): DragVisuals["glow"] {
  const glowAllowed =
    input.enabled && input.showHoverHighlight && !input.isDragging;
  if (glowAllowed) {
    const hoverGlow = input.isHovered && !input.isDragActive;
    if (hoverGlow || marked) return "full";
    if (armed) return "outline";
  }
  return "none";
}

/** What the shared canvas cursor should show, if this part claims it. */
function resolveCursor(
  input: DragVisualsInput,
  engagedHover: boolean,
): DragVisuals["cursor"] {
  if (input.draggingThis) return "grabbing";
  if (engagedHover && !input.activateOnly) return "move";
  return null;
}

/**
 * Resolves the visual layers of a draggable part. Three layers, three
 * questions, so that no layer has to mean two things:
 *
 * - *Which parts does this step concern?* Every armed part - one the caller
 *   set to a plain drag, i.e. a part of the step the user is on - draws a thin
 *   **outline** through the case without being pointed at, so the user can
 *   find every part they are meant to be placing, including the ones on the
 *   far side, and nothing more: a dozen armed parts must stay readable.
 * - *Which one am I pointing at?* The **full glow** is reserved for attention:
 *   the pointer on the part, or the host pointing it out (its row hovered in a
 *   list). Its armed siblings keep their outline rather than stepping back;
 *   the difference in strength is what makes the pointed-out part stand out.
 * - *Which one is active, and which way does it move?* The **arrows** belong
 *   to the part the host has selected, and to the part being dragged. In
 *   direct mode the selected part is also the one a plain press drags: a
 *   press on any other armed part is a click that selects it, or an orbit.
 *   The arrows are the handles for an axis-locked drag, so a hover with the
 *   modifier down reveals them in either mode - that is the point at which
 *   such a drag could actually start.
 *
 * While the camera orbits, the pointer is on no part at all: every layer that
 * follows the hover drops back to what the part shows unhovered.
 *
 * Where a part *may* go is a different question and stays under the pointer:
 * the movement outline, the guide lines and the `move` cursor appear only
 * once the part is engaged - hovered while a press would drag it, or dragged.
 * So hovering an armed part that is not selected looks exactly like the host
 * pointing it out: the glow grows, nothing else.
 */
export function resolveDragVisuals(raw: DragVisualsInput): DragVisuals {
  const input = { ...raw, isHovered: raw.isHovered && !raw.cameraOrbiting };
  // Activate-only parts are never marked: a drag cannot start on them in
  // direct mode, so a permanent mark would advertise a move that never
  // happens. The host's attention follows the same exclusions.
  const armed =
    input.activation === "direct" && input.enabled && !input.activateOnly;
  const marked = input.pointedOut && input.enabled && !input.activateOnly;

  // Whether a press right now would start a drag. Mirrors the real pointer-down
  // gate (`requireModifier` in `DraggablePart`), so no hint advertises a drag
  // the user cannot perform.
  const dragArmed =
    (input.activation === "direct" && input.selected) || input.modifierHeld;

  // Pointer on the part with a drag armed, and nothing else being dragged.
  const engagedHover = input.isHovered && dragArmed && !input.isDragActive;

  // Pointer on the part with the modifier down: the arrows are reachable as
  // handles, so they are shown in either mode.
  const modifierHover =
    input.isHovered && input.modifierHeld && !input.isDragActive;

  const glow = resolveGlow(input, armed, marked);

  return {
    glow,
    // Only the parts the user is working on shine through the case. A bare
    // hover glow marks what is under the pointer, which is visible by
    // definition. Stable for an armed part between idle and hover on purpose:
    // the glow keys its x-ray occlusion slot on it.
    glowXray: glow !== "none" && (armed || marked),
    arrows:
      !input.activateOnly &&
      (input.draggingThis ||
        (input.selected && input.enabled && !input.isDragActive) ||
        modifierHover),
    guides:
      !input.activateOnly && (input.isDragging || input.debug || engagedHover),
    cursor: resolveCursor(input, engagedHover),
  };
}
